import { Cpu, Users, Wrench, Activity, ChevronDown, Volume2, VolumeX, AudioLines, Lock, Ear, MoreVertical, Menu, BookOpen, LifeBuoy, BellRing, Keyboard } from "lucide-react";
import { MODELS, VOICES } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function ChatHeader({ mode, setMode, model, setModel, voiceEnabled, setVoiceEnabled, speaking, voice, setVoice, wakeWord, onToggleWakeWord, wakeSupported, staff, onOpenStaff, onOpenSidebar, onOpenHelp, onEnableReminders, onOpenCommands }) {
  const activeModel = MODELS.find((m) => m.id === model);
  const activeVoice = VOICES.find((v) => v.id === voice) || VOICES[0];
  const isTech = mode === "technician";

  return (
    <header
      data-testid="chat-header"
      className="flex items-center gap-2 md:gap-3 px-3 md:px-6 h-16 ios-safe-top border-b border-[var(--border)] glass shrink-0"
    >
      <button
        data-testid="open-sidebar-button"
        onClick={onOpenSidebar}
        title="Menu & history"
        className="lg:hidden h-10 w-10 shrink-0 grid place-items-center rounded-xl border border-[var(--border)] bg-black/20 text-[var(--text-secondary)] hover:text-white transition-colors"
      >
        <Menu size={18} />
      </button>

      <div className="flex items-center gap-2.5 shrink-0">
        <div className="leading-tight">
          <p className="font-display font-bold text-lg md:text-xl text-[var(--primary)] logo-glow">it IN A bit</p>
          <p className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--text-muted)] hidden sm:block">&gt;&gt; APEX · AI ASSISTANT</p>
        </div>
      </div>

      {/* Mode toggle — Tech Co-Pilot only visible to signed-in staff */}
      {staff ? (
        <div className="ml-1 md:ml-4 flex items-center rounded-xl border border-[var(--border)] p-1 bg-black/20 shrink-0">
          <button
            data-testid="mode-toggle-customer"
            onClick={() => setMode("customer")}
            className={`flex items-center gap-1.5 rounded-lg px-2.5 md:px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
              !isTech ? "bg-[var(--primary)] text-black" : "text-[var(--text-secondary)] hover:text-white"
            }`}
          >
            <Users size={13} /> <span className="hidden sm:inline">Customer</span>
          </button>
          <button
            data-testid="mode-toggle-technician"
            onClick={() => setMode("technician")}
            className={`flex items-center gap-1.5 rounded-lg px-2.5 md:px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
              isTech ? "bg-[var(--tech-accent)] text-black" : "text-[var(--text-secondary)] hover:text-white"
            }`}
          >
            <Wrench size={13} /> <span className="hidden sm:inline">Tech Co-Pilot</span>
          </button>
        </div>
      ) : (
        <div className="ml-1 md:ml-4 flex items-center gap-1.5 rounded-xl border border-[var(--border)] px-3 py-2 bg-black/20 shrink-0 text-xs font-semibold text-[var(--text-secondary)]">
          <Users size={13} /> <span className="hidden sm:inline">Customer</span>
        </div>
      )}

      <div className="flex-1" />

      {/* Status */}
      <div className="hidden min-[2000px]:flex items-center gap-2 text-[11px] font-mono-tech text-[var(--text-secondary)] mr-1 shrink-0">
        <Activity size={13} className="text-[var(--tech-accent)]" />
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[var(--tech-accent)] pulse-dot inline-block" /> RMM ONLINE</span>
      </div>

      <div className="hidden md:flex items-center gap-2 md:gap-3 shrink-0">
      {/* Help & guides (everyone) */}
      <button
        data-testid="help-open-button"
        onClick={onOpenHelp}
        title="Help & guides — self-help for common issues"
        className="flex items-center gap-1.5 rounded-xl border border-[var(--border)] bg-black/20 px-2.5 py-2 text-xs font-semibold text-[var(--text-secondary)] transition-colors duration-200 hover:border-[var(--primary)] hover:text-white shrink-0"
      >
        <LifeBuoy size={14} className="text-[var(--primary)]" />
        <span className="hidden min-[2200px]:inline">Help</span>
      </button>

      {/* Knowledge Base (signed-in tech only) */}
      {staff?.is_tech && (
        <button
          data-testid="knowledge-base-button"
          onClick={onOpenStaff}
          title="Open the Tech Knowledge Base"
          className="flex items-center gap-1.5 rounded-xl border border-[var(--tech-accent)]/60 bg-[var(--tech-accent)]/10 px-2.5 py-2 text-xs font-semibold text-[var(--tech-accent)] transition-colors duration-200 hover:bg-[var(--tech-accent)]/20 shrink-0"
        >
          <BookOpen size={14} />
          <span className="hidden min-[2200px]:inline">Knowledge Base</span>
        </button>
      )}

      {/* Voice/chat command cheat-sheet (signed-in tech only) */}
      {staff?.is_tech && (
        <button
          data-testid="commands-open-button"
          onClick={onOpenCommands}
          title="What can I say? — Apex commands"
          className="grid place-items-center h-9 w-9 rounded-xl border border-[var(--border)] bg-black/20 text-[var(--primary)] transition-colors duration-200 hover:border-[var(--primary)] shrink-0"
        >
          <Keyboard size={15} />
        </button>
      )}

      {/* 5pm reminders opt-in (signed-in tech only) */}
      {staff?.is_tech && (
        <button
          data-testid="reminders-enable-button"
          onClick={onEnableReminders}
          title="Turn on notifications (new jobs + 5pm reminders) on this device"
          className="grid place-items-center h-9 w-9 rounded-xl border border-[var(--border)] bg-black/20 text-[var(--primary)] transition-colors duration-200 hover:border-[var(--primary)] shrink-0"
        >
          <BellRing size={15} />
        </button>
      )}

      {/* Voice toggle */}
      <button
        data-testid="voice-toggle-button"
        onClick={() => setVoiceEnabled((v) => !v)}
        title={voiceEnabled ? "Voice replies on" : "Voice replies off"}
        className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs font-semibold transition-colors duration-200 shrink-0 ${
          voiceEnabled ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-secondary)] bg-black/20 hover:text-white"
        }`}
      >
        {voiceEnabled ? <Volume2 size={14} className={speaking ? "pulse-dot" : ""} /> : <VolumeX size={14} />}
        <span className="hidden md:inline">{voiceEnabled ? "Voice On" : "Voice Off"}</span>
      </button>

      {/* Wake word toggle */}
      {wakeSupported && (
        <button
          data-testid="wake-word-toggle-button"
          onClick={onToggleWakeWord}
          title={wakeWord ? "Wake word on — say \u201cHey Apex\u201d" : "Enable wake word (\u201cHey Apex\u201d)"}
          className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs font-semibold transition-colors duration-200 shrink-0 ${
            wakeWord ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-secondary)] bg-black/20 hover:text-white"
          }`}
        >
          <Ear size={14} className={wakeWord ? "pulse-dot" : ""} />
          <span className="hidden min-[2200px]:inline">{wakeWord ? "\u201cHey Apex\u201d" : "Wake Word"}</span>
        </button>
      )}

      {/* Voice picker */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="voice-picker-dropdown"
            disabled={!voiceEnabled}
            title="Choose bot voice"
            className="flex items-center gap-1.5 rounded-xl border border-[var(--border)] bg-black/20 px-2.5 py-2 text-xs font-semibold text-[var(--text-primary)] transition-colors duration-200 hover:border-[var(--primary)] disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            <AudioLines size={14} className="text-[var(--primary)]" />
            <span className="hidden min-[2200px]:inline">{activeVoice.name}</span>
            <ChevronDown size={13} className="text-[var(--text-muted)]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
          {VOICES.map((v) => (
            <DropdownMenuItem
              key={v.id}
              data-testid={`voice-option-${v.id}`}
              onClick={() => setVoice(v.id)}
              className="flex flex-col items-start gap-0.5 cursor-pointer focus:bg-white/8"
            >
              <span className={`text-sm font-semibold ${v.id === voice ? "text-[var(--primary)]" : ""}`}>{v.name}</span>
              <span className="text-[10px] text-[var(--text-muted)] font-mono-tech">{v.badge}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Model selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="model-selector-dropdown"
            className="flex items-center gap-2 rounded-xl border border-[var(--border)] bg-black/20 px-3 py-2 text-xs font-semibold text-[var(--text-primary)] transition-colors duration-200 hover:border-[var(--primary)] shrink-0"
          >
            <Cpu size={14} className="text-[var(--primary)]" />
            <span className="hidden sm:inline">{activeModel?.name}</span>
            <ChevronDown size={13} className="text-[var(--text-muted)]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
          {MODELS.map((m) => (
            <DropdownMenuItem
              key={m.id}
              data-testid={`model-option-${m.id}`}
              onClick={() => setModel(m.id)}
              className="flex flex-col items-start gap-0.5 cursor-pointer focus:bg-white/8"
            >
              <span className={`text-sm font-semibold ${m.id === model ? "text-[var(--primary)]" : ""}`}>{m.name}</span>
              <span className="text-[10px] text-[var(--text-muted)] font-mono-tech">{m.badge}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      </div>

      {/* Mobile overflow menu — collapses voice / wake / voice picker / model */}
      <div className="md:hidden shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="header-overflow-menu"
              title="More options"
              className="h-10 w-10 rounded-xl border border-[var(--border)] bg-black/20 grid place-items-center text-[var(--text-secondary)] hover:text-white transition-colors"
            >
              <MoreVertical size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
            <DropdownMenuItem data-testid="m-help-open" onClick={onOpenHelp} className="cursor-pointer focus:bg-white/8 flex items-center gap-2">
              <LifeBuoy size={15} className="text-[var(--primary)]" /> Help &amp; guides
            </DropdownMenuItem>
            {staff?.is_tech && (
              <DropdownMenuItem data-testid="m-knowledge-base" onClick={onOpenStaff} className="cursor-pointer focus:bg-white/8 flex items-center gap-2">
                <BookOpen size={15} className="text-[var(--tech-accent)]" /> Knowledge Base
              </DropdownMenuItem>
            )}
            {staff?.is_tech && (
              <DropdownMenuItem data-testid="m-commands-open" onClick={onOpenCommands} className="cursor-pointer focus:bg-white/8 flex items-center gap-2">
                <Keyboard size={15} className="text-[var(--primary)]" /> Commands (what can I say?)
              </DropdownMenuItem>
            )}
            {staff?.is_tech && (
              <DropdownMenuItem data-testid="m-reminders-enable" onClick={onEnableReminders} className="cursor-pointer focus:bg-white/8 flex items-center gap-2">
                <BellRing size={15} className="text-[var(--primary)]" /> Turn on job alerts &amp; reminders
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-[var(--border)]" />
            <DropdownMenuItem data-testid="m-voice-toggle" onClick={() => setVoiceEnabled((v) => !v)} className="cursor-pointer focus:bg-white/8 flex items-center gap-2">
              {voiceEnabled ? <Volume2 size={15} className="text-[var(--primary)]" /> : <VolumeX size={15} />}
              {voiceEnabled ? "Voice replies: On" : "Voice replies: Off"}
            </DropdownMenuItem>
            {wakeSupported && (
              <DropdownMenuItem data-testid="m-wake-toggle" onClick={onToggleWakeWord} className="cursor-pointer focus:bg-white/8 flex items-center gap-2">
                <Ear size={15} className={wakeWord ? "text-[var(--primary)]" : ""} />
                {wakeWord ? "Wake word: On" : "Wake word: Off"}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-[var(--border)]" />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="focus:bg-white/8 data-[state=open]:bg-white/8">
                <AudioLines size={15} className="text-[var(--primary)] mr-2" /> Voice: {activeVoice.name}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
                {VOICES.map((v) => (
                  <DropdownMenuItem key={v.id} data-testid={`m-voice-option-${v.id}`} onClick={() => setVoice(v.id)}
                    className={`cursor-pointer focus:bg-white/8 ${v.id === voice ? "text-[var(--primary)]" : ""}`}>{v.name}</DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="focus:bg-white/8 data-[state=open]:bg-white/8">
                <Cpu size={15} className="text-[var(--primary)] mr-2" /> Model: {activeModel?.name}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]">
                {MODELS.map((m) => (
                  <DropdownMenuItem key={m.id} data-testid={`m-model-option-${m.id}`} onClick={() => setModel(m.id)}
                    className={`cursor-pointer focus:bg-white/8 ${m.id === model ? "text-[var(--primary)]" : ""}`}>{m.name}</DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <button
        data-testid="staff-open-button"
        onClick={onOpenStaff}
        title={staff ? `Signed in as ${staff.name || staff.email}` : "Staff sign in (Tech / Accounts)"}
        className={`ml-1 flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs font-semibold transition-colors duration-200 shrink-0 ${
          staff
            ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10"
            : "border-[var(--border)] text-[var(--text-secondary)] bg-black/20 hover:border-[var(--primary)] hover:text-white"
        }`}
      >
        <Lock size={14} />
        <span className="hidden md:inline max-w-[9rem] truncate">{staff ? (staff.name || staff.email) : "Staff"}</span>
      </button>
    </header>
  );
}
